import { useState } from "react";
import { Link } from "react-router";
import type { Option } from "../../api/content";
import type { PaidReadyView } from "../../api/paidReady";
import type { KycStatus } from "../../api/types";
import { Banner } from "../../components/Banner";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { OptionTile } from "../../components/OptionTile";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { inputShell } from "../../components/Field";
import { FileButton } from "../../components/FileButton";

const KYC_LABELS: [KycStatus, string][] = [
  ["verified", "Verified"],
  ["pending", "Submitted, awaiting approval"],
  ["notstarted", "Not started"],
];
const KYC_MAX_BYTES = 8 * 1024 * 1024;

interface PaidReadyScreenProps {
  view: PaidReadyView;
  payMethods: Option[];
  uploadingPlatform: string | null;
  uploadError: string | null;
  busy: boolean;
  onPayment: (methods: string[], confirmed: boolean) => void;
  onKycStatus: (platformId: string, status: KycStatus) => void;
  onKycFile: (platformId: string, file: File) => void;
  onComplete: () => void;
}

/** Step 4: two parts, in any order. Payment set up and KYC proof make the participant Work-Enabled. */
export function PaidReadyScreen(props: PaidReadyScreenProps) {
  const { view } = props;
  const [fileError, setFileError] = useState<string | null>(null);
  const methods = new Set(view.step.payMethods);
  const payOk = methods.size > 0 && view.step.payConfirmed;
  const kycOk = view.profiles.some(
    (p) => p.hasKycScreenshot && p.kycStatus && p.kycStatus !== "notstarted",
  );

  const pickFile = (platformId: string, file: File | undefined) => {
    setFileError(null);
    if (!file) return;
    if (!file.type.startsWith("image/"))
      return setFileError("That needs to be an image (a screenshot).");
    if (file.size === 0 || file.size > KYC_MAX_BYTES)
      return setFileError(
        file.size === 0 ? "That file looks empty." : "That file is too large, keep it under 8MB.",
      );
    props.onKycFile(platformId, file);
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 4 · Verify"
        title="Get verified, and become Work-Enabled."
        sub="Two parts, in any order. When both are done you're Work-Enabled and get your Launch Plan."
      />
      <Guide guideKey="gig4" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />

      <Section
        title="1. Set up how you get paid"
        hint="Pick every method you've actually set up. Cross-border clients usually pay through Payoneer, Grey, Juicyway or Wise."
        status={<Pill ok={payOk} />}
      >
        <div className="flex flex-col gap-2">
          {props.payMethods.map((method) => (
            <OptionTile
              key={method.k}
              selected={methods.has(method.k)}
              title={method.t}
              detail={method.d}
              onToggle={() => {
                const next = new Set(methods);
                if (next.has(method.k)) next.delete(method.k);
                else next.add(method.k);
                props.onPayment(Array.from(next), view.step.payConfirmed);
              }}
            />
          ))}
        </div>
        <div className="mt-3">
          <OptionTile
            selected={view.step.payConfirmed}
            title="My payment account is verified and can receive money"
            detail="You've completed the provider's own ID checks and your account is active"
            onToggle={() => props.onPayment(Array.from(methods), !view.step.payConfirmed)}
          />
        </div>
      </Section>

      <Section
        title="2. Show your KYC status on your platform"
        hint="Upload a screenshot of your identity verification status on at least one platform you joined. Status must be verified or submitted."
        status={<Pill ok={kycOk} />}
      >
        {view.profiles.length === 0 ? (
          <Banner tone="warn">
            No verified platform profile found. Go back to{" "}
            <Link to="/gig/1" className="underline">
              Step 1
            </Link>{" "}
            and verify at least one profile first.
          </Banner>
        ) : (
          view.profiles.map((profile) => (
            <div
              key={profile.id}
              className="mt-2.5 rounded-[var(--radius-control)] border border-border p-3.5"
            >
              <div className="font-bold">{profile.platform.name}</div>
              <div className="text-[12.5px] text-text-muted">{profile.profileUrl}</div>
              <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-[12.5px] font-bold">
                  KYC status on {profile.platform.name}
                  <select
                    className={`${inputShell} mt-1.5`}
                    value={profile.kycStatus ?? ""}
                    onChange={(event) =>
                      props.onKycStatus(profile.platform.id, event.target.value as KycStatus)
                    }
                  >
                    <option value="" disabled>
                      Choose...
                    </option>
                    {KYC_LABELS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="text-[12.5px] font-bold">
                  Screenshot of your KYC status
                  <div className="mt-1.5">
                    <FileButton
                      label="Upload screenshot"
                      ariaLabel={`Upload your ${profile.platform.name} KYC screenshot`}
                      accept="image/*"
                      busy={props.uploadingPlatform === profile.platform.id}
                      hasFile={profile.hasKycScreenshot}
                      onFile={(file) => pickFile(profile.platform.id, file)}
                    />
                  </div>
                </div>
              </div>
              <p className="mt-2 text-[12.5px] text-text-muted">
                {props.uploadingPlatform === profile.platform.id
                  ? "Uploading..."
                  : profile.hasKycScreenshot
                    ? `✓ ${profile.kycFileName ?? "Screenshot"} on file.`
                    : "PNG or JPG, under 8MB. Show the page where the platform says your identity is verified or under review."}
              </p>
              {profile.kycStatus === "notstarted" ? (
                <Banner tone="warn">
                  You can't be paid until KYC is done. Start it on {profile.platform.name} now, then
                  update this.
                </Banner>
              ) : null}
            </div>
          ))
        )}
        {fileError || props.uploadError ? (
          <p role="alert" className="mt-2 text-[13px] text-negative">
            {fileError ?? props.uploadError}
          </p>
        ) : null}
      </Section>

      {view.done ? (
        <Banner tone="good">
          ✓ Step 4 complete. You're Work-Enabled.{" "}
          <Link to="/launch-plan" className="underline">
            My Launch Plan
          </Link>
        </Banner>
      ) : view.gate.pass ? (
        <Banner tone="good">✓ Both parts done. Confirm to become Work-Enabled.</Banner>
      ) : null}
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Confirm Work-Enabled"
        next={{ to: "/gig/5", label: "Continue to Step 5" }}
        busy={props.busy}
        onComplete={props.onComplete}
      />
    </div>
  );
}
